import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SubMenuService } from '../../../../services/submenu.service';
import { WaypointService } from '../../../../services/waypoint.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { Subscription } from 'rxjs';
import { getWaypointIconAsset, resolveWaypointType } from '../../../../shared/utils/waypoint-icon.util';

interface WatchItem {
  id: string;
  waypointId?: string;
  position?: any;
  waypointDetails?: any;
  tripId?: string;
  /** Primary display name: fleet name when fleet post, author name otherwise */
  name: string;
  avatarUrl?: string;
  avatarInitials: string;
  /** Always the author/user name (used as secondary line for fleet posts) */
  authorName: string;
  authorAvatarUrl?: string;
  authorAvatarInitials: string;
  /** Set when the post was shared through a fleet/community */
  communityName?: string;
  communityAvatarUrl?: string;
  subtitle: string;
  time: string;
  tag: string;
  tagIcon: string;
  tagTone?: 'colored' | 'mono';
  postType: 'trips' | 'waypoint';
  description?: string;
  descriptionExpanded?: boolean;
  hasLongDescription?: boolean;
  section: 'Today' | 'Last Week' | 'Earlier This Month' | 'Last Month';
  sortTs: number;
  create_ts?: string;
  tripData?: any;
}

interface WatchSection {
  label: 'Today' | 'Last Week' | 'Earlier This Month' | 'Last Month';
  items: WatchItem[];
}

@Component({
  selector: 'app-watch-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './watch-page.component.html',
  styleUrls: ['./watch-page.component.scss'],
})
export class WatchPageComponent implements OnInit, OnDestroy {
  isOpen = false;
  feedItems: WatchItem[] = [];
  groupedFeedItems: WatchSection[] = [];
  loading = false;
  loadingMore = false;
  hasMore = true;
  currentPage = 1;
  readonly pageSize = 20;
  skeletonItems = Array(5);
  private currentUserId: string | null = null;
  private currentUserAuthId: string | null = null;
  private subs = new Subscription();

  // Hide-from-me state
  hideConfirmItem: WatchItem | null = null;
  hideInProgress = false;
  toastMessage: string | null = null;
  toastColor: 'green' | 'red' = 'green';
  private toastTimer: any = null;
  private locallyHiddenPostIds = new Set<string>();
  private locallyHiddenTripIds = new Set<string>();

  constructor(
    private submenuService: SubMenuService,
    private waypointService: WaypointService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.currentUserId = this.authService.getUserObjectId();
    this.currentUserAuthId = this.authService.getUserIdFromToken();

    // Preload the first page silently so data is ready before the panel opens.
    this.fetchSharedPosts(false);

    this.subs.add(this.submenuService.watchPageModalState$.subscribe((state) => {
      this.isOpen = state;
      if (state && this.feedItems.length === 0 && !this.loading) {
        this.fetchSharedPosts(false);
      }
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  fetchSharedPosts(loadMore: boolean = false) {
    if (loadMore) return; // Auto-streaming handles all pages; ignore manual load-more calls.
    if (this.loading) return;

    this.loading = true;
    this.currentPage = 1;
    this.feedItems = [];
    this.groupedFeedItems = [];
    this.hasMore = true;
    this.cdr.detectChanges();

    this.streamAllPages();
  }

  private streamAllPages(): void {
    const myIds = [this.currentUserId, this.currentUserAuthId].filter(Boolean);
    const seenIds = new Set<string>();
    const maxPages = 50;
    let page = 1;

    const fetchNext = () => {
      if (page > maxPages) {
        this.loading = false;
        this.loadingMore = false;
        this.cdr.detectChanges();
        return;
      }

      const isFirstPage = page === 1;
      if (!isFirstPage) {
        this.loadingMore = true;
        this.cdr.detectChanges();
      }

      this.waypointService.getWatch(page, this.pageSize).subscribe({
        next: (res: any) => {
          let items: any[] = [];
          if (Array.isArray(res)) {
            items = res;
          } else if (res?.results && Array.isArray(res.results)) {
            items = res.results;
          }

          const processed: WatchItem[] = items
            .filter((post: any) => {
              const id = (post.id || post._id || '').toString().trim();
              if (id && seenIds.has(id)) return false;
              if (id) seenIds.add(id);

              const creatorId = post.audit_data?.create_user_id || post.waypoint_details?.audit_data?.create_user_id;
              const isMine = creatorId && myIds.includes(creatorId);
              const postType = post.post_type;
              if (postType !== 'waypoint' && postType !== 'trips') return false;

              if (post.action_type === 'shared') return true;
              if (isMine) return false;

              const privacy = (postType === 'trips'
                ? post.trip_details?.privacy
                : post.waypoint_details?.privacy
              || 'public').toLowerCase();
              return privacy === 'public' || privacy === 'custom';
            })
            .map((post: any): WatchItem => {
              const authorName = post.source_profile?.name || post.creator_name || 'Someone';
              const authorAvatarUrl: string | undefined = post.source_profile?.image_url || post.source_profile?.profile_image || post.source_profile?.avatar || post.source_profile?.photo || undefined;
              const authorAvatarInitials = this.getInitials(authorName);

              // Fleet/community detection: mirrors Flutter's _isCommunityRefAFleet —
              // use source_community when its name differs from the author's name.
              const communityRaw = post.source_community || post.community_ref;
              const communityNameRaw: string = (communityRaw?.name || '').trim();
              const isCommunityFleet = communityNameRaw.length > 0 &&
                communityNameRaw.toLowerCase() !== authorName.toLowerCase();
              const communityName = isCommunityFleet ? communityNameRaw : undefined;
              const communityAvatarUrl: string | undefined = isCommunityFleet
                ? (communityRaw?.image_url || communityRaw?.avatar || undefined)
                : undefined;

              // Primary display name: fleet name when fleet post, author otherwise
              const displayName = communityName ?? authorName;
              const displayAvatarUrl = communityAvatarUrl ?? authorAvatarUrl;
              const displayAvatarInitials = communityName
                ? this.getInitials(communityName)
                : authorAvatarInitials;

              const isTrip = post.post_type === 'trips';
              const wpDetails = post.waypoint_details || {};
              const tripDetails = post.trip_details || {};
              const waypointId =
                wpDetails.waypoint_id ||
                wpDetails._id ||
                wpDetails.id ||
                post.waypoint_ref?.identifier ||
                post.waypoint_ref?.reference_id ||
                null;
              let tag = 'Post';
              let tagIcon = 'assets/home/filter/fishcaught_logo.svg';
              let tagTone: 'colored' | 'mono' = 'mono';
              let subtitle = isTrip ? 'Fishing trip' : 'Waypoint';
              let description = '';

              if (isTrip) {
                tag = 'Trip';
                tagIcon = 'assets/common_icons/watch_boat.svg';
                tagTone = 'mono';
                subtitle = tripDetails.name || post.content?.headline || 'Trip started!';
                description = tripDetails.description || post.content?.text || '';
              } else if (post.post_type === 'waypoint') {
                tag = this.getWaypointFeedTag(wpDetails);
                tagIcon = getWaypointIconAsset(wpDetails, 'card');
                tagTone = 'colored';
                subtitle = `${authorName} published a new waypoint: ${tag}`;
              }

              const rawDate = post.audit_data?.create_ts || post.created_at;
              const d = rawDate ? new Date(rawDate) : null;
              const timeStr = d ? d.toLocaleString('en-US', {
                month: 'long',
                day: 'numeric',
              }) + ' at ' + d.toLocaleString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }) : '';

              return {
                id: post.id || post._id || Math.random().toString(),
                waypointId: isTrip ? undefined : waypointId || undefined,
                position: isTrip ? undefined : (wpDetails.position || post.waypoint_ref?.position),
                waypointDetails: isTrip ? undefined : (Object.keys(wpDetails).length ? wpDetails : undefined),
                tripId: isTrip ? (tripDetails._id || tripDetails.id || tripDetails.trip_id || post.trip_ref?.identifier || post.trip_ref?.reference_id || post.content?.trip_id || null) : undefined,
                name: displayName,
                avatarUrl: displayAvatarUrl,
                avatarInitials: displayAvatarInitials,
                authorName,
                authorAvatarUrl,
                authorAvatarInitials,
                communityName,
                communityAvatarUrl,
                subtitle,
                time: timeStr,
                tag,
                tagIcon,
                tagTone,
                postType: post.post_type,
                description,
                descriptionExpanded: false,
                hasLongDescription: (description || '').length > 120,
                section: this.getWatchSectionLabel(d),
                sortTs: d ? d.getTime() : 0,
                create_ts: rawDate,
                tripData: isTrip ? tripDetails : undefined,
              };
            });

          if (processed.length > 0) {
            this.feedItems = [...this.feedItems, ...processed].sort((a, b) => b.sortTs - a.sortTs);
            this.groupedFeedItems = this.groupWatchItems(
              this.feedItems.filter((i) => !this.isHidden(i))
            );
          }

          // Determine whether there are more pages
          let hasNext = false;
          if (res?.next && res.next !== 'null') {
            hasNext = true;
          } else if (res?.count !== undefined) {
            hasNext = this.feedItems.length < res.count;
          } else if (!Array.isArray(res)) {
            hasNext = items.length === this.pageSize;
          }

          this.hasMore = hasNext;

          if (isFirstPage) {
            this.loading = false;
          } else {
            this.loadingMore = false;
          }
          this.cdr.detectChanges();

          if (hasNext && items.length > 0) {
            page++;
            fetchNext();
          } else {
            this.loading = false;
            this.loadingMore = false;
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          console.error('Error fetching watch feed page', page, err);
          this.loading = false;
          this.loadingMore = false;
          this.cdr.detectChanges();
        }
      });
    };

    fetchNext();
  }

  close() {
    this.submenuService.setWatchPageModalState(false);
  }

  onHideClick(item: WatchItem, event: Event): void {
    event.stopPropagation();
    this.hideConfirmItem = item;
  }

  cancelHide(): void {
    this.hideConfirmItem = null;
  }

  confirmHide(): void {
    const item = this.hideConfirmItem;
    if (!item) return;
    this.hideConfirmItem = null;

    // Optimistic removal from watch feed list
    this.locallyHiddenPostIds.add(item.id);
    if (item.tripId) this.locallyHiddenTripIds.add(item.tripId);
    this.groupedFeedItems = this.groupWatchItems(
      this.feedItems.filter((i) => !this.isHidden(i))
    );
    this.cdr.detectChanges();

    // Immediately remove from map (mirrors Flutter's Phase 1 / hideNotifier signal)
    this.submenuService.notifyWatchItemHidden({
      tripId: item.tripId,
      waypointId: item.waypointId,
    });

    // Skip API call when there is no identifiable trip/waypoint ID
    if (!item.tripId && !item.waypointId) {
      this.showToast('Deleted for you.', 'green');
      return;
    }

    this.hideInProgress = true;
    this.waypointService.hideForMe(item.tripId, item.waypointId).subscribe({
      next: () => {
        this.hideInProgress = false;
        this.showToast('Deleted for you.', 'green');
        // Background reload to sync backend-filtered state
        this.fetchSharedPosts(false);
      },
      error: () => {
        // Roll back optimistic removal from feed list
        this.locallyHiddenPostIds.delete(item.id);
        if (item.tripId) this.locallyHiddenTripIds.delete(item.tripId);
        this.groupedFeedItems = this.groupWatchItems(
          this.feedItems.filter((i) => !this.isHidden(i))
        );
        this.hideInProgress = false;
        this.showToast('Could not hide this item. Please try again.', 'red');
        this.cdr.detectChanges();
      }
    });
  }

  private isHidden(item: WatchItem): boolean {
    if (this.locallyHiddenPostIds.has(item.id)) return true;
    if (item.tripId && this.locallyHiddenTripIds.has(item.tripId)) return true;
    return false;
  }

  private showToast(message: string, color: 'green' | 'red'): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastMessage = message;
    this.toastColor = color;
    this.cdr.detectChanges();
    this.toastTimer = setTimeout(() => {
      this.toastMessage = null;
      this.cdr.detectChanges();
    }, 3000);
  }

  onItemMapClick(item: WatchItem, event: Event) {
    event.stopPropagation();
    this.close();

    if (item.postType === 'trips') {
      const tripId = item.tripId || item.id;
      this.submenuService.pendingTripData = item.tripData || null;
      this.submenuService.focusOnMap('trip', tripId, null, item.create_ts, item.tripData, 'watch');
      return;
    }

    const waypointId = item.waypointId || item.id;
    this.submenuService.focusOnMap('waypoint', waypointId, item.position, item.create_ts, undefined, 'watch', item.waypointDetails);
  }

  toggleTripDescription(item: WatchItem, event: Event): void {
    event.stopPropagation();
    item.descriptionExpanded = !item.descriptionExpanded;
  }

  private getWatchSectionLabel(date: Date | null): WatchItem['section'] {
    if (!date) return 'Last Month';

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfItemDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((startOfToday.getTime() - startOfItemDay.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return 'Today';
    if (diffDays <= 7) return 'Last Week';
    if (date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()) return 'Earlier This Month';
    return 'Last Month';
  }

  private groupWatchItems(items: WatchItem[]): WatchSection[] {
    const order: WatchSection['label'][] = ['Today', 'Last Week', 'Earlier This Month', 'Last Month'];
    return order
      .map((label) => ({
        label,
        items: items.filter((item) => item.section === label),
      }))
      .filter((section) => section.items.length > 0);
  }

  private getFishCaughtSubtype(wpDetails: any): string | null {
    const rawSubtype = (
      wpDetails?.details?.fish_subtype ||
      wpDetails?.details?.fishSubtype ||
      wpDetails?.fish_subtype ||
      wpDetails?.fishSubtype ||
      wpDetails?.subtype ||
      ''
    ).toString().trim().toLowerCase();

    if (rawSubtype.includes('yellow')) return 'Yellow Fin';
    if (rawSubtype.includes('blue')) return 'Blue Fin';
    if (rawSubtype.includes('long')) return 'Long Fin';
    if (rawSubtype.includes('bigeye')) return 'Bigeye';
    if (rawSubtype.includes('white marlin') || rawSubtype.includes('whitemarlin')) return 'White Marlin';
    if (rawSubtype.includes('blue marlin') || rawSubtype.includes('bluemarlin')) return 'Blue Marlin';
    return null;
  }

  private getFishCaughtQuantity(wpDetails: any): number {
    const rawQty = (
      wpDetails?.details?.fish_quantity ??
      wpDetails?.details?.fishQuantity ??
      wpDetails?.fish_quantity ??
      wpDetails?.fishQuantity ??
      1
    );
    const qty = Number(rawQty);
    if (!Number.isFinite(qty)) return 1;
    return Math.max(1, Math.min(99, Math.trunc(qty)));
  }

  private getWaypointFeedTag(wpDetails: any): string {
    const type = resolveWaypointType(wpDetails);

    if (type === 'fish_caught') {
      const fishSubtype = this.getFishCaughtSubtype(wpDetails) || 'Fish Caught';
      const qty = this.getFishCaughtQuantity(wpDetails);
      return qty > 1 ? `${fishSubtype} x${qty}` : fishSubtype;
    }

    if (type === 'canyon') {
      return 'Spot';
    }

    if (type === 'inlet') {
      return 'Inlet';
    }

    if (type) {
      return type.replace(/_/g, ' ').replace(/\b\w/g, (char: string) => char.toUpperCase());
    }

    return 'Waypoint';
  }

  private getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  }
}
